import { useState } from 'react';
import { KpiCard } from '../components/KpiCard';
import { Tag } from '../components/Tag';
import type { AdminResponse, AdminShowroomRow, CaptureStaffRow, VpageStaffChatRow } from '../types';
import { asNumber, num, statusLabel, uniq } from '../utils';
import { RANGE_OPTIONS } from '../ranges';

interface AdminScreenProps {
  data: AdminResponse | null;
  loaded: boolean;
  busy: boolean;
  range: string;
  onRange: (range: string) => void;
}


// chốt / mua hàng — tô màu như StaffScreen
const crTone = (pct: number): 'green' | 'amber' | 'red' => (pct >= 50 ? 'green' : pct >= 30 ? 'amber' : 'red');
// độ phủ báo cáo / Palexy (thang nhỏ, vài %)
const coverTone = (pct: number): 'green' | 'amber' | 'red' => (pct >= 6 ? 'green' : pct >= 3 ? 'amber' : 'red');
const ageTone = (d: number): 'red' | 'amber' | 'gray' => (d >= 3 ? 'red' : d >= 1 ? 'amber' : 'gray');
// % xin SĐT / Palexy (trên lượt khách): mục tiêu ~20-30%
const sdtTone = (pct: number): 'green' | 'amber' | 'red' => (pct >= 20 ? 'green' : pct >= 10 ? 'amber' : 'red');
const rate = (a: number, b: number): number => (b ? Math.round((1000 * a) / b) / 10 : 0);

export function AdminScreen({ data, loaded, busy, range, onRange }: AdminScreenProps) {
  const [view, setView] = useState<'showroom' | 'staff'>('showroom');
  const [store, setStore] = useState('');

  if (busy && !loaded) return <div className="card empty">Đang tải dữ liệu điều hành...</div>;
  if (!loaded) return <div className="card empty">Mở module Điều hành để tải dữ liệu.</div>;

  const showroom: AdminShowroomRow[] = data?.showroom || [];
  const staffAll: CaptureStaffRow[] = data?.staff || [];
  const chatAll: VpageStaffChatRow[] = data?.staffChat || [];
  const alerts = data?.alerts || [];
  const palexyOk = data?.meta?.palexy_ok !== false;

  const stores = uniq([...showroom.map(r => r.store_code), ...staffAll.map(r => r.store_code || '')]);

  const tot = showroom.reduce(
    (a, r) => ({
      visits: a.visits + asNumber(r.visits),
      bao_cao: a.bao_cao + asNumber(r.bao_cao),
      mua_hang: a.mua_hang + asNumber(r.mua_hang),
      chot: a.chot + asNumber(r.chot),
      service: a.service + asNumber(r.service),
      vpage: a.vpage + asNumber(r.hoi_thoai_vpage),
      canh_bao: a.canh_bao + asNumber(r.canh_bao),
    }),
    { visits: 0, bao_cao: 0, mua_hang: 0, chot: 0, service: 0, vpage: 0, canh_bao: 0 },
  );
  const alertTotal = asNumber(data?.alertSummary?.total) || tot.canh_bao;

  // chăm Vpage theo nhân viên (key = tên|store)
  const chatMap = new Map<string, VpageStaffChatRow>();
  chatAll.forEach(r => chatMap.set(`${r.staff_name || ''}|${r.store_code || ''}`, r));
  // số ca cảnh báo theo nhân viên
  const alertByStaff = new Map<string, number>();
  alerts.forEach(a => {
    const k = `${a.staff_name || ''}|${a.store_code || ''}`;
    alertByStaff.set(k, (alertByStaff.get(k) || 0) + 1);
  });

  const staffRows = staffAll.filter(r => !store || r.store_code === store);
  const alertRows = alerts.filter(a => !store || a.store_code === store);

  // Phễu xin SĐT vs Palexy: gồm MỌI showroom có dữ liệu Palexy (KHÔNG loại NCV).
  // MISS SĐT = walk-in chưa ghi nhận (max(0, Palexy−báo cáo)) + đã báo nhưng thiếu SĐT (báo cáo−có SĐT).
  const sdtRows = showroom.filter(r => asNumber(r.visits) > 0);
  const sdtTot = sdtRows.reduce(
    (a, r) => {
      const v = asNumber(r.visits), bc = asNumber(r.bao_cao), cs = asNumber(r.co_sdt);
      return {
        visits: a.visits + v,
        co_sdt: a.co_sdt + cs,
        khong_bao_cao: a.khong_bao_cao + Math.max(0, v - bc),
        bao_thieu: a.bao_thieu + Math.max(0, bc - cs),
      };
    },
    { visits: 0, co_sdt: 0, khong_bao_cao: 0, bao_thieu: 0 },
  );
  const missSdt = sdtTot.khong_bao_cao + sdtTot.bao_thieu;
  const palexyMax = data?.meta?.palexy_max_date;

  return (
    <>
      <section className="grid kpis">
        <KpiCard label="Lượt khách Palexy" value={num(tot.visits)} sub={palexyOk ? `${showroom.length} showroom` : 'DWH chưa kết nối'} tone={palexyOk ? 'cyan' : 'gray'} />
        <KpiCard label="Báo cáo Agent" value={num(tot.bao_cao)} sub={`phủ ${rate(tot.bao_cao, tot.visits)}% lượt khách`} tone="blue" />
        <KpiCard label="Hội thoại Vpage" value={num(tot.vpage)} sub="chăm sóc qua chat" tone="violet" />
        <KpiCard label="Chốt (mua)" value={num(tot.chot)} sub={`CR vận hành ${rate(tot.chot, tot.mua_hang)}%`} tone="green" />
        <KpiCard label="⚠ Cảnh báo chăm" value={num(alertTotal)} sub="chưa chốt+SĐT · ko Vpage" tone={alertTotal ? 'red' : 'green'} />
      </section>

      <section className="filters">
        <div className="actions start">
          <span className="kpi-label">Khoảng</span>
          {RANGE_OPTIONS.map(o => (
            <button key={o.v} className={`tab ${range === o.v ? 'active' : ''}`} onClick={() => onRange(o.v)}>{o.l}</button>
          ))}
        </div>
        <div className="actions start">
          <span className="kpi-label">Góc nhìn</span>
          <button className={`tab ${view === 'showroom' ? 'active' : ''}`} onClick={() => setView('showroom')}>Showroom</button>
          <button className={`tab ${view === 'staff' ? 'active' : ''}`} onClick={() => setView('staff')}>Nhân viên</button>
        </div>
        {view === 'staff' && (
          <div className="actions start">
            <span className="kpi-label">Store</span>
            <button className={`tab ${!store ? 'active' : ''}`} onClick={() => setStore('')}>Tất cả</button>
            {stores.map(s => <button key={s} className={`tab ${store === s ? 'active' : ''}`} onClick={() => setStore(s)}>{s}</button>)}
          </div>
        )}
      </section>

      <section className="card" style={{ borderLeft: '4px solid var(--cyan)' }}>
        <div className="section-title">
          <h2>📵 Phễu xin SĐT vs Palexy · MISS bao nhiêu SĐT so với lượt khách</h2>
          <span>
            Miss SĐT = lượt khách Palexy (căn từ ngày báo cáo) − số khách xin được SĐT.
            {palexyMax ? ` Palexy đến ${palexyMax.slice(8)}/${palexyMax.slice(5, 7)} (trễ ~1 ngày).` : ''} visits = lượt khách (gồm vãng lai) → miss là chặn trên.
          </span>
        </div>
        <div className="grid kpis" style={{ marginBottom: 14 }}>
          <KpiCard label="Lượt khách (Palexy căn)" value={num(sdtTot.visits)} sub={`${sdtRows.length} showroom · khách đến quầy`} tone="cyan" />
          <KpiCard label="Xin được SĐT" value={num(sdtTot.co_sdt)} sub={`${rate(sdtTot.co_sdt, sdtTot.visits)}% lượt khách`} tone="green" />
          <KpiCard label="MISS SĐT" value={num(missSdt)} sub="không có SĐT để chăm" tone="red" />
          <KpiCard label="↳ Không báo cáo (ẩn)" value={num(sdtTot.khong_bao_cao)} sub="sale không ghi nhận" tone="amber" />
          <KpiCard label="↳ Báo thiếu SĐT" value={num(sdtTot.bao_thieu)} sub="sale tự báo" tone="gray" />
        </div>
        <div className="scroll">
          <table>
            <thead><tr>
              <th>Store</th><th className="right">Palexy (căn)</th><th className="right">Có SĐT</th><th>chốt/chưa/sv</th>
              <th className="right">% xin SĐT</th><th className="right">MISS SĐT</th><th className="right">↳ Không báo cáo</th><th className="right">↳ Báo thiếu SĐT</th>
            </tr></thead>
            <tbody>{showroom.map(r => {
              const visits = asNumber(r.visits);
              const coSdt = asNumber(r.co_sdt);
              const baoCao = asNumber(r.bao_cao);
              const baoThieu = Math.max(0, baoCao - coSdt);
              const compo = `${num(asNumber(r.chot_co_sdt))}/${num(asNumber(r.chua_chot_co_sdt))}/${num(asNumber(r.service_co_sdt))}`;
              if (!visits) {
                return (
                  <tr key={r.store_code}>
                    <td><b>{r.store_code}</b><div className="muted">{r.store_name}</div></td>
                    <td className="right muted" colSpan={2}>Palexy chưa có dữ liệu</td>
                    <td className="muted">{compo}</td>
                    <td className="right muted">-</td><td className="right muted">-</td><td className="right muted">-</td>
                    <td className="right tnum muted">{num(baoThieu)}</td>
                  </tr>
                );
              }
              const pXin = rate(coSdt, visits);
              const khongBaoCao = Math.max(0, visits - baoCao);
              const miss = khongBaoCao + baoThieu;
              return (
                <tr key={r.store_code}>
                  <td><b>{r.store_code}</b><div className="muted">{r.store_name}</div></td>
                  <td className="right tnum">{num(visits)}</td>
                  <td className="right tnum"><b>{num(coSdt)}</b></td>
                  <td className="muted">{compo}</td>
                  <td className="right"><Tag tone={sdtTone(pXin)}>{pXin}%</Tag></td>
                  <td className="right tnum"><b>{num(miss)}</b></td>
                  <td className="right tnum">{num(khongBaoCao)}</td>
                  <td className="right tnum muted">{num(baoThieu)}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      </section>

      {view === 'showroom' ? (
        <section className="card">
          <div className="section-title">
            <h2>Phễu rò rỉ theo showroom · Palexy → báo cáo → Vpage → mua</h2>
            <span>Độ phủ báo cáo = báo cáo ÷ lượt khách Palexy · CR mua/Palexy = chốt ÷ lượt khách · Palexy theo ngày (DWH){palexyOk ? '' : ' · ⚠ DWH chưa kết nối'}</span>
          </div>
          <div className="scroll">
            <table>
              <thead><tr>
                <th>Store</th><th className="right">Palexy</th><th className="right">Báo cáo</th><th className="right">Phủ b/c</th>
                <th className="right">Vpage khớp</th><th className="right">Hội thoại Vpage</th><th className="right">Mua</th><th className="right">Chốt</th>
                <th className="right">Service</th><th className="right">CR mua/Palexy</th><th className="right">CR vận hành</th><th className="right">⚠ Cảnh báo</th>
              </tr></thead>
              <tbody>{showroom.map(r => {
                const visits = asNumber(r.visits);
                const baoCao = asNumber(r.bao_cao);
                const cover = rate(baoCao, visits);
                const crMua = rate(asNumber(r.chot), visits);
                const crVan = rate(asNumber(r.chot), asNumber(r.mua_hang));
                const cb = asNumber(r.canh_bao);
                return (
                  <tr key={r.store_code}>
                    <td><b>{r.store_code}</b><div className="muted">{r.store_name}</div></td>
                    <td className="right tnum">{visits ? num(visits) : <span className="muted">-</span>}</td>
                    <td className="right tnum"><b>{num(baoCao)}</b></td>
                    <td className="right">{visits ? <Tag tone={coverTone(cover)}>{cover}%</Tag> : <span className="muted">-</span>}</td>
                    <td className="right tnum">{num(asNumber(r.capture_co_vpage))}</td>
                    <td className="right tnum">{num(asNumber(r.hoi_thoai_vpage))}</td>
                    <td className="right tnum">{num(asNumber(r.mua_hang))}</td>
                    <td className="right tnum"><b>{num(asNumber(r.chot))}</b></td>
                    <td className="right tnum muted">{num(asNumber(r.service))}</td>
                    <td className="right">{visits ? `${crMua}%` : <span className="muted">-</span>}</td>
                    <td className="right"><Tag tone={crTone(crVan)}>{crVan}%</Tag></td>
                    <td className="right">{cb ? <Tag tone="red">{num(cb)}</Tag> : <span className="muted">0</span>}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        </section>
      ) : (
        <section className="card">
          <div className="section-title">
            <h2>Theo nhân viên {store ? `· ${store}` : '· tất cả showroom'}</h2>
            <span>{num(staffRows.length)} nhân viên · CR = chốt ÷ mua hàng · chăm Vpage &amp; số ca cảnh báo theo từng sale</span>
          </div>
          <div className="scroll">
            <table>
              <thead><tr>
                <th>Store</th><th>Nhân viên</th><th className="right">Báo cáo</th><th className="right">Mua</th><th className="right">Chốt</th>
                <th className="right">Chưa chốt +SĐT</th><th className="right">Service</th><th className="right">CR%</th>
                <th className="right">Tin Vpage</th><th className="right">Kết bạn</th><th className="right">⚠ Cảnh báo</th>
              </tr></thead>
              <tbody>{staffRows.map(r => {
                const key = `${r.staff_name || ''}|${r.store_code || ''}`;
                const chat = chatMap.get(key);
                const cb = alertByStaff.get(key) || 0;
                const cr = asNumber(r.cr_pct);
                return (
                  <tr key={(r.staff_id || '') + '|' + (r.store_code || '')}>
                    <td><b>{r.store_code || '-'}</b></td>
                    <td>{r.staff_name || <span className="muted">(chưa map)</span>}</td>
                    <td className="right tnum"><b>{num(asNumber(r.bao_cao))}</b></td>
                    <td className="right tnum">{num(asNumber(r.mua_hang))}</td>
                    <td className="right tnum"><b>{num(asNumber(r.chot))}</b></td>
                    <td className="right tnum">{num(asNumber(r.chua_chot_co_sdt))}</td>
                    <td className="right tnum muted">{num(asNumber(r.service))}</td>
                    <td className="right"><Tag tone={crTone(cr)}>{cr}%</Tag></td>
                    <td className="right tnum">{chat ? num(asNumber(chat.tin_shop_gui)) : <span className="muted">-</span>}</td>
                    <td className="right tnum">{chat ? num(asNumber(chat.khach_moi)) : <span className="muted">-</span>}</td>
                    <td className="right">{cb ? <Tag tone="red">{num(cb)}</Tag> : <span className="muted">0</span>}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        </section>
      )}

      <section className="card" style={{ borderLeft: '4px solid var(--red)' }}>
        <div className="section-title">
          <h2>🔴 Cảnh báo chăm sóc Vpage {store ? `· ${store}` : ''}</h2>
          <span>
            {num(alertRows.length)} ca · "chưa chốt + có SĐT" nhưng SĐT khách KHÔNG khớp hội thoại Vpage nào → sale chưa chủ động kết bạn/chăm.
            <b> Directional</b> (Vpage chỉ ~22% hội thoại có SĐT).
          </span>
        </div>
        {alertRows.length === 0 ? (
          <div className="card empty">Không có ca cảnh báo trong khoảng đã chọn.</div>
        ) : (
          <div className="scroll">
            <table>
              <thead><tr>
                <th>Store</th><th>Nhân viên</th><th>Khách</th><th>SĐT</th><th>Nhu cầu</th><th>Trạng thái</th><th>Ngày báo</th><th className="right">Trôi (ngày)</th>
              </tr></thead>
              <tbody>{alertRows.map(a => {
                const d = asNumber(a.days_since);
                return (
                  <tr key={a.capture_id}>
                    <td><b>{a.store_code || '-'}</b></td>
                    <td>{a.staff_name || <span className="muted">(chưa map)</span>}</td>
                    <td>{a.customer_name || <span className="muted">-</span>}</td>
                    <td className="tnum">{a.phone_mask || '-'}</td>
                    <td className="muted" style={{ maxWidth: 280 }}>{a.need_text || '-'}</td>
                    <td><Tag tone="amber">{statusLabel(a.store_care_status)}</Tag></td>
                    <td className="tnum muted">{a.ngay}</td>
                    <td className="right"><Tag tone={ageTone(d)}>{num(d)}d</Tag></td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
